
import React, { useState } from 'react';
import { X, Minus, Pentagon } from 'lucide-react';
import type { DrawingToolType } from '../../hooks/useDrawingMode';
import { ROUTE_COLOR_PALETTE } from '../../constants';
import { useTranslation } from 'react-i18next';

interface DrawingSaveDialogProps {
  toolType: DrawingToolType;
  formattedDistance: string;
  formattedArea: string;
  onSave: (name: string, color: string, note: string, strokeWidth: number, strokeStyle: 'solid' | 'dashed' | 'dotted') => void;
  onCancel: () => void;
}

const COLORS = ROUTE_COLOR_PALETTE.slice(0, 8);

const DrawingSaveDialog: React.FC<DrawingSaveDialogProps> = ({
  toolType, formattedDistance, formattedArea, onSave, onCancel,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [color, setColor] = useState(toolType === 'line' ? '#3b82f6' : '#8b5cf6');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [strokeStyle, setStrokeStyle] = useState<'solid' | 'dashed' | 'dotted'>('solid');

  const measurement = toolType === 'area' ? formattedArea : formattedDistance;
  const typeLabel = toolType === 'line' ? 'Linje' : 'Omrade';
  const Icon = toolType === 'line' ? Minus : Pentagon;

  const handleSave = () => {
    onSave(name.trim() || (toolType === 'line' ? 'Min linje' : 'Mitt omrade'), color, note.trim(), strokeWidth, strokeStyle);
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-end md:items-center justify-center pointer-events-none">
      <div className="pointer-events-auto w-full max-w-sm mx-2 bg-white rounded-t-3xl md:rounded-3xl md:mb-4 shadow-2xl border border-stone-200 animate-in slide-in-from-bottom-2 max-h-[85dvh] overflow-y-auto" style={{ overscrollBehavior: 'contain', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <div className="flex items-center gap-2">
            <Icon size={16} style={{ color }} strokeWidth={toolType === 'line' ? 3 : 2} />
            <h3 className="text-[13px] font-black text-stone-800">Lagre {typeLabel.toLowerCase()}</h3>
          </div>
          <button onClick={onCancel} className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-muted)]">
            <X size={16} />
          </button>
        </div>

        {/* Measurement */}
        <div className="px-5 pb-3">
          <span className="text-2xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-lg">
            {typeLabel}: {measurement}
          </span>
        </div>

        <div className="px-5 pb-5 space-y-3">
          {/* Name */}
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={toolType === 'line' ? 'Min linje' : 'Mitt omrade'}
            className="w-full px-3 py-2.5 bg-stone-50 rounded-xl text-[12px] text-stone-800 placeholder-[var(--text-secondary)] border border-stone-200 outline-none focus:border-emerald-400"
            autoFocus
          />

          {/* Note */}
          <input
            type="text"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={t("auto.map.drawingsavedialog.placeholder.notat_valgfritt", "Notat (valgfritt)")}
            className="w-full px-3 py-2.5 bg-stone-50 rounded-xl text-[12px] text-stone-800 placeholder-[var(--text-secondary)] border border-stone-200 outline-none focus:border-emerald-400"
          />

          {/* Color picker */}
          <div>
            <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">{t("auto.map.drawingsavedialog.farge", "Farge")}</p>
            <div className="flex gap-2">
              {COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-10 h-10 rounded-full border-2 transition-all ${
                    color === c ? 'border-stone-800 scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          {/* Stroke style (lines only) */}
          {toolType === 'line' && (
            <div>
              <p className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">{t("auto.map.drawingsavedialog.linjestil", "Linjestil")}</p>
              <div className="flex gap-2">
                {(['solid', 'dashed', 'dotted'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setStrokeStyle(s)}
                    className={`flex-1 py-2 rounded-xl text-2xs font-bold border transition-colors ${
                      strokeStyle === s
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                        : 'border-stone-200 text-[var(--text-muted)] hover:border-stone-300'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <svg width="30" height="4" className="mb-px">
                        <line
                          x1="0" y1="2" x2="30" y2="2"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeDasharray={s === 'dashed' ? '6,4' : s === 'dotted' ? '2,4' : 'none'}
                        />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={onCancel}
              className="flex-1 py-2.5 bg-stone-100 text-[var(--text-muted)] rounded-xl text-[12px] font-bold hover:bg-stone-200 transition-colors"
            >
              {t("auto.map.drawingsavedialog.avbryt", "Avbryt")}
            </button>
            <button
              onClick={handleSave}
              className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-[12px] font-bold hover:bg-emerald-600 active:scale-95 transition-all"
            >
              {t("auto.map.drawingsavedialog.lagre", "Lagre")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrawingSaveDialog;
